// eslint-disable-next-line no-restricted-imports -- this is the wrapper itself
import * as Apollo from '@apollo/client';
import {NetworkStatus} from '@apollo/client';
import type {LazyQueryHookOptions, NoInfer} from '@apollo/client/react/types/types';
import {useEffect} from 'react';

import {useRequestAnalyzer} from 'core/RequestAnalyzer/RequestAnalyzer';

// eslint-disable-next-line no-restricted-imports -- this is the wrapper itself
export * from '@apollo/client';

function useLazyQueryWrapper<TData = any, TVariables extends Apollo.OperationVariables = Apollo.OperationVariables>(
  query: Apollo.DocumentNode | Apollo.TypedDocumentNode<TData, TVariables>,
  options?: LazyQueryHookOptions<NoInfer<TData>, NoInfer<TVariables>>
) {
  const {addQuery} = useRequestAnalyzer();

  useEffect(() => {
    addQuery(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return Apollo.useLazyQuery(query, options);
}

export const useLazyQuery = useLazyQueryWrapper as typeof Apollo.useLazyQuery;

function useMutationWrapper<TData = any, TVariables extends Apollo.OperationVariables = Apollo.OperationVariables>(
  mutation: Apollo.DocumentNode | Apollo.TypedDocumentNode<TData, TVariables>,
  options?: Apollo.MutationHookOptions<TData, TVariables>
) {
  const {addMutation} = useRequestAnalyzer();

  useEffect(() => {
    addMutation(mutation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return Apollo.useMutation(mutation, options);
}

export const useMutation = useMutationWrapper as typeof Apollo.useMutation;

type CustomQueryHookOptions = {
  disableSkipSetsLoadingState?: boolean;
};
export type QueryHookOptions<
  TData = any,
  TVariables extends Apollo.OperationVariables = Apollo.OperationVariables
> = Apollo.QueryHookOptions<TData, TVariables> & CustomQueryHookOptions;

function useQueryWrapper<TData = any, TVariables extends Apollo.OperationVariables = Apollo.OperationVariables>(
  query: Apollo.DocumentNode | Apollo.TypedDocumentNode<TData, TVariables>,
  options: QueryHookOptions<TData, TVariables> = {}
): Apollo.QueryResult<TData, TVariables> {
  const {addQuery} = useRequestAnalyzer();
  const {skip, disableSkipSetsLoadingState} = options;
  const result = Apollo.useQuery(query, options);

  useEffect(() => {
    addQuery(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {previousData, loading, networkStatus} = result;

  const isSkippedWithNoData = !disableSkipSetsLoadingState && skip && previousData === undefined;

  const loadingComputed = isSkippedWithNoData ? true : loading;
  const networkStatusComputed = isSkippedWithNoData ? NetworkStatus.loading : networkStatus;

  return {
    ...result,
    loading: loadingComputed,
    networkStatus: networkStatusComputed,
  };
}

export const useQuery = useQueryWrapper;
